import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import * as types from './types';
import * as extract from 'extractFromState';
import * as searchActions from './searchActions';
import * as dtUtil from 'util/decisionTreeUtil';
import { getMacroById } from 'api/macroArticles';
import { showError } from '../util/errorPopupUtil';
import { isValidPartnerReachOutTicket } from 'util/ticketSelector';
import { searchOrders, fetchCommonOrder } from 'actions/tools/CommonAsyncOrderActions';
import { getOrdersSearchProperties } from 'selectors/tools';
import { extractInProgressTreeAndWizardLocation, getBrandOfSelectedTicket } from 'extractFromState';
import { selectOrder, setSelectedItem } from 'actions/tools/OrdersSearchActions';

export const setInProgressTreeAndWizardLocation = (ticketId) => (dispatch, getState) => {
  const state = getState();
  ticketId = ticketId || extract.extractNonHistoricalTicketIdFromState(state);

  // Only store for assigned tickets - clearing only occurs for assigned
  if (!ticketId || !extract.isTicketAssigned(state, ticketId)) {
    return;
  }

  // We avoid storing the previous path if it was reset, if it was not reset, we store the previous path
  const storedLocation = extractInProgressTreeAndWizardLocation(state, ticketId);
  const previousWizardData = storedLocation?.macroWizard || {};
  const {
    tagPath: previousTagPath = '',
    decisionTreePath: previousDecisionTreePath = '',
    pathWasReset: previousPathWasReset = false,
  } = previousWizardData;

  // Do not pull all macroWizard data - trees, macros and tags may update
  const macroWizard = {
    tagPath: getTagPath(state, previousTagPath),
    decisionTreePath: getDecisionTreePath(state, previousDecisionTreePath),
    snippetTextList: get(state, 'tools.macroWizard.snippetTextList', []),
    selectedMacroId: get(state, 'tools.macroWizard.selectedMacro.objectId'),
    pathWasReset: get(state, 'tools.macroWizard.pathWasReset', false) || previousPathWasReset,
  };

  // Order search info will not update
  const data = {
    macroWizard,
    ordersSearch: get(state, 'tools.ordersSearch', {}),
    subscriptionSearch: get(state, 'tools.subscriptionSearch', {}),
  };

  return dispatch({
    type: types.SET_IN_PROGRESS_TREE_AND_WIZARD_LOCATION,
    ticketId,
    data,
  });
};

const getTagPath = (state, previousTagPath) => {
  const pathWasReset = get(state, 'tools.macroWizard.pathWasReset', false);
  const previousPath = pathWasReset ? '' : previousTagPath;

  return get(state, 'tools.macroWizard.tagPath', '') || previousPath;
};

const getDecisionTreePath = (state, previousDecisionTreePath) => {
  const pathWasReset = get(state, 'tools.macroWizard.pathWasReset', false);
  const previousPath = pathWasReset ? '' : previousDecisionTreePath;

  return get(state, 'tools.macroWizard.decisionTreePath', '') || previousPath;
};

export const hydrateInProgressTreeAndWizardForPartnerReachOut = (ticketId) => async (dispatch, getState) => {
  if (!ticketId) {
    return;
  }

  let state = getState();
  const ticket = extract.extractTicketFromStateById(state, ticketId);
  const allPaths = extract.extractAllMacroWizardPaths(state);
  const decisionTree = extract.extractDecisionTreeOfSelectedTicket(state);
  const simplrAccountId = extract.getSimplrAccountIdOfSelectedTicket(state);

  if (!isValidPartnerReachOutTicket(ticket)) {
    return dispatch(searchActions.setPathBasedOffTags());
  }

  const {
    selectedOrderNumber,
    selectedItemIds,
    flowPath: decisionTreePath,
    simplrTag: tagPath,
  } = ticket.pendingPartnerReachOut;

  // validate stored location data - trees or tags may have been updated
  const tagPathStillExists = !!tagPath && allPaths.indexOf(tagPath) > -1;
  const pathContainsFullTag = !!tagPath && tagPath.includes('--');
  const decisionTreeRecord = !!tagPath && decisionTree && decisionTree[tagPath];

  const decisionTreePathStillValid =
    decisionTreePath &&
    pathContainsFullTag &&
    !isEmpty(decisionTreeRecord) &&
    dtUtil.treeNodeExistsAtPath(decisionTreeRecord, decisionTreePath);

  const persistPathDataStillValid = tagPathStillExists && decisionTreePathStillValid;

  if (pathContainsFullTag && persistPathDataStillValid) {
    await dispatch(searchActions.setMacroWizardPath(tagPath));

    if (selectedOrderNumber) {
      await dispatch(searchOrders({ customerEmail: ticket.customerEmail }));
      state = getState(); // needs an update to get updated orders
      const ordersSearch = getOrdersSearchProperties(state);
      const brand = getBrandOfSelectedTicket(state);
      const orders = ordersSearch.orders || [];
      if (orders.length) {
        const foundOrder = orders.find((order) => order.orderNumber === selectedOrderNumber) || orders[0];
        const conversationId = extract.extractNonHistoricalTicketIdFromState(state);
        const order = await fetchCommonOrder(String(simplrAccountId), foundOrder, conversationId, brand);
        dispatch(selectOrder(order));
        if (Array.isArray(selectedItemIds)) {
          selectedItemIds.forEach((itemId) => dispatch(setSelectedItem(itemId)));
        }
      }
    }

    dispatch(searchActions.setDecisionTreePath(decisionTreePath));
  } else {
    dispatch(searchActions.setPathBasedOffTags());
  }
};

export const hydrateInProgressTreeAndWizardLocation = (ticketId) => async (dispatch, getState) => {
  if (!ticketId) {
    return;
  }

  const state = getState();
  const locationData = extract.extractInProgressTreeAndWizardLocation(state, ticketId);
  const allPaths = extract.extractAllMacroWizardPaths(state);
  const decisionTree = extract.extractDecisionTreeOfSelectedTicket(state);
  const macrosByTagPath = extract.extractMacroListByPath(state);
  const simplrAccountId = extract.getSimplrAccountIdOfSelectedTicket(state);

  if (!locationData) {
    return dispatch(searchActions.setPathBasedOffTags());
  }

  // macro wizard data
  const decisionTreePath = get(locationData, 'macroWizard.decisionTreePath', '');
  const tagPath = get(locationData, 'macroWizard.tagPath', '');
  const snippetTextList = get(locationData, 'macroWizard.snippetTextList', []);
  const selectedMacroId = get(locationData, 'macroWizard.selectedMacroId');

  // order & subscription search decision tree data
  const ordersSearch = get(locationData, 'ordersSearch');
  const subscriptionSearch = get(locationData, 'subscriptionSearch');

  // validate stored location data - trees or tags may have been updated
  const tagPathStillExists = !!tagPath && allPaths.indexOf(tagPath) > -1;
  const pathContainsFullTag = !!tagPath && tagPath.includes('--');
  const decisionTreeRecord = !!tagPath && decisionTree && decisionTree[tagPath];

  let decisionTreePathStillValid = true;
  if (decisionTreePath && pathContainsFullTag) {
    decisionTreePathStillValid =
      !isEmpty(decisionTreeRecord) && dtUtil.treeNodeExistsAtPath(decisionTreeRecord, decisionTreePath);
  }

  // Find up-to-date macro version - macro may have been edited or deleted
  let previouslySelectedMacro = null;
  if (selectedMacroId && pathContainsFullTag) {
    let upToDateMacro;

    // First, check in the wizard
    const macrosOnTagPath = macrosByTagPath[tagPath] || [];
    upToDateMacro = macrosOnTagPath.find((macro) => macro.objectId === selectedMacroId);

    // Second, check in the selected tree
    if (!upToDateMacro && !isEmpty(decisionTreeRecord)) {
      const macroIsUsedInTree = dtUtil.macroIsUsedInTree(decisionTreeRecord, selectedMacroId);

      if (macroIsUsedInTree) {
        try {
          upToDateMacro = await getMacroById(selectedMacroId, simplrAccountId);
        } catch (error) {
          showError(error);
        }
      }
    }

    previouslySelectedMacro = upToDateMacro || null;
  }

  const macroDataStillValid = !selectedMacroId || (!!selectedMacroId && !isEmpty(previouslySelectedMacro));
  const persistPathDataStillValid = tagPathStillExists && decisionTreePathStillValid && macroDataStillValid;

  if (pathContainsFullTag && persistPathDataStillValid && !isEmpty(ordersSearch)) {
    // Must wait to set macro wizard path in order to avoid clearing decisionTreePath
    await dispatch(searchActions.setMacroWizardPath(tagPath));
    dispatch(searchActions.setOrderSearch(ordersSearch));
    dispatch(searchActions.setSubscriptionSearch(subscriptionSearch));
    dispatch(searchActions.setSnippetText(snippetTextList));
    dispatch(searchActions.setDecisionTreePath(decisionTreePath));
    // Must set macro after tree path or the macro will be cleared
    dispatch(searchActions.setSelectedWizardMacro(previouslySelectedMacro));
  } else {
    dispatch(searchActions.setPathBasedOffTags());
  }
};

export const clearInProgressTreeAndWizardLocation = (ticketId) => ({
  type: types.CLEAR_IN_PROGRESS_TREE_AND_WIZARD_LOCATION,
  ticketId,
});
